import type { SmartPricingAnalyticsTier } from "@src/constants/billing";

import type { BillingCommandStatus, BillingGateTier } from "../types";
import type {
  BillingCommandIntent,
  OptimisticBillingEvent,
  PlannedBillingCommand,
} from "./commandTypes";
import { applyOptimisticEvent, isOptimisticEventConfirmed } from "./optimistic";
import {
  type BillingCommandMessage,
  isMatchingCommandMessage,
  isTerminalCommandFailure,
} from "./reconciliation";
import type { BillingSnapshot, SelectionState } from "./types";

// apply-coupon is dispatched directly, not via request-entity-action.
// The pause dialog quotes the boundary from the live snapshot when it renders
// and confirms, so opening it carries no date.
type EntityAction =
  | Exclude<
      BillingCommandIntent,
      | { type: "submit-selection" }
      | { type: "apply-coupon" }
      | { type: "pause" }
    >
  | Readonly<{ type: "pause"; entityId: string }>;

export type BillingWorkflowDialogState =
  | Readonly<{ type: "closed" }>
  | Readonly<{ type: "plan-change"; selection: SelectionState }>
  | Readonly<{
      type: "smart-pricing-kickoff";
      selection: SelectionState;
      // "plan-change" was reached by submitting the module-selection dialog and
      // returns there when cancelled; "direct" skipped that dialog (high-volume
      // add from a discovery card) and closes outright when cancelled.
      origin: "plan-change" | "direct";
    }>
  | Readonly<{
      type: "smart-pricing-whats-next";
      orderVolumeTier: BillingGateTier;
      tierPurchased: SmartPricingAnalyticsTier;
    }>
  | Readonly<{
      type: "cancel";
      entityId: string;
      expectedCancellationAt: number;
      timing: "immediate" | "scheduled";
    }>
  | Readonly<{ type: "pause"; entityId: string }>
  | Readonly<{
      type: "cancel-core-module";
      entityId: string;
      expectedCancellationAt: number;
      planEntityId: string;
      step: "warning" | "confirm";
    }>
  | Readonly<{ type: "remove-module"; entityId: string }>
  | Readonly<{ type: "cancel-trial"; entityId: string }>
  | Readonly<{
      type: "command-blocked";
      acknowledgeLabel: string;
      body: string;
      title: string;
      recovery?: "start-new-plan";
    }>;

type ActiveCommand = Readonly<{
  awaitsNamedShopifyApproval: boolean;
  commandId: string | null;
  orgId: string;
  overlay: OptimisticBillingEvent;
  plan: PlannedBillingCommand;
  status: Exclude<BillingCommandStatus, "idle">;
}>;

export type BillingWorkflowCommandState =
  | Readonly<{ status: "idle" }>
  | ActiveCommand;

export type StagedCoupon = Readonly<{
  id: string;
  percentOff?: number;
  amountOff?: number;
}>;

export type ShopifyApprovalKind = "initial_subscription" | "cap_raise";

export type ApprovalReturnOutcome =
  | "activated"
  | "committed"
  | "declined"
  | "expired"
  | "timed_out";

/**
 * The merchant coming back from a Blocking Approval. It is a sibling of the
 * command state, not a variant of it: the return arrives on a fresh page load,
 * so it has neither the plan nor the optimistic overlay an `ActiveCommand`
 * requires.
 */
export type ApprovalReturnState =
  | Readonly<{ status: "idle" }>
  | Readonly<{
      status: "waiting";
      armedAt: number;
      approvalKind: ShopifyApprovalKind;
      // The settle endpoint answers first; the read model is only polled once
      // it reports an approval the snapshot has yet to show, or no answer yet.
      pollsSnapshot: boolean;
    }>
  | Readonly<{ status: "settled"; outcome: ApprovalReturnOutcome }>;

export type BillingWorkflowState = Readonly<{
  approvalReturn: ApprovalReturnState;
  command: BillingWorkflowCommandState;
  dialog: BillingWorkflowDialogState;
  // Pre-trial coupon, carried into the create request on submit and reflected
  // in the plan-change dialog's total in the meantime. Cleared on dismiss, on
  // an apply-coupon command-start, and once any command settles.
  stagedCoupon: StagedCoupon | null;
}>;

export type BillingWorkflowAction =
  | Readonly<{ type: "open-plan-change"; selection: SelectionState }>
  | Readonly<{ type: "preselect-plan-change"; selection: SelectionState }>
  | Readonly<{
      type: "preselect-smart-pricing-kickoff";
      selection: SelectionState;
    }>
  | Readonly<{ type: "open-smart-pricing-kickoff" }>
  | Readonly<{ type: "cancel-smart-pricing-kickoff" }>
  | Readonly<{
      type: "show-smart-pricing-whats-next";
      orderVolumeTier: BillingGateTier;
      tierPurchased: SmartPricingAnalyticsTier;
    }>
  | Readonly<{ type: "update-selection"; selection: SelectionState }>
  | Readonly<{ type: "stage-coupon"; coupon: StagedCoupon | null }>
  | Readonly<{ type: "request-entity-action"; action: EntityAction }>
  | Readonly<{
      type: "open-core-module-cancel";
      entityId: string;
      expectedCancellationAt: number;
      planEntityId: string;
    }>
  | Readonly<{ type: "advance-core-module-cancel" }>
  | Readonly<{ type: "dismiss-dialog" }>
  | Readonly<{ type: "acknowledge-blocked" }>
  | Readonly<{ type: "organization-changed" }>
  | Readonly<{
      type: "show-command-blocked";
      acknowledgeLabel: string;
      body: string;
      title: string;
      recovery?: "start-new-plan";
    }>
  | Readonly<{
      type: "command-started";
      orgId: string;
      plan: PlannedBillingCommand;
    }>
  | Readonly<{ type: "command-accepted"; commandId: string }>
  | Readonly<{
      type: "approval-return-armed";
      approvalKind: ShopifyApprovalKind;
      armedAt: number;
    }>
  | Readonly<{
      type: "approval-return-settled";
      outcome: ApprovalReturnOutcome;
    }>
  | Readonly<{ type: "approval-return-polling" }>
  | Readonly<{ type: "shopify-approval-named" }>
  | Readonly<{ type: "shopify-approval-timed-out" }>
  | Readonly<{ type: "raw-snapshot-observed"; snapshot: BillingSnapshot }>
  | Readonly<{ type: "server-confirmed" }>
  | Readonly<{ type: "mutation-failed" }>
  | Readonly<{ type: "websocket-observed"; message: BillingCommandMessage }>
  | Readonly<{
      type: "terminal-command-failure";
      commandId: string;
      orgId: string;
    }>;

const CLOSED: BillingWorkflowDialogState = { type: "closed" };
const IDLE: BillingWorkflowCommandState = { status: "idle" };
const NO_APPROVAL_RETURN: ApprovalReturnState = { status: "idle" };

const blockedDialog = (): BillingWorkflowDialogState => ({
  type: "command-blocked",
  title: "Billing change in progress",
  body: "Your recent billing change is still being applied. Once it completes, you'll be able to make this change.",
  acknowledgeLabel: "Got it",
});

export const initialBillingWorkflowState: BillingWorkflowState = {
  approvalReturn: NO_APPROVAL_RETURN,
  command: IDLE,
  dialog: CLOSED,
  stagedCoupon: null,
};

const isActive = (
  command: BillingWorkflowCommandState
): command is ActiveCommand => command.status !== "idle";

const blockIfActive = (
  state: BillingWorkflowState
): BillingWorkflowState | null =>
  isActive(state.command) ? { ...state, dialog: blockedDialog() } : null;

const dialogForEntityAction = (
  action: EntityAction
): BillingWorkflowDialogState => {
  switch (action.type) {
    case "cancel":
      return { ...action };
    case "pause":
      return { ...action };
    case "remove-module":
      return { ...action };
    case "cancel-trial":
      return { ...action };
    case "release":
      return CLOSED;
    case "cancel-pending-removal":
      return CLOSED;
    case "cancel-pending-pause":
      return CLOSED;
  }
};

// The staged coupon lives exactly as long as the dialog that collected it. Once
// the command it rode along with settles, the subscription carries the coupon in
// its own operator stack, so leaving it staged would fold the same discount into
// the displayed total twice.
const finishCommand = (state: BillingWorkflowState): BillingWorkflowState => ({
  ...state,
  command: IDLE,
  dialog: CLOSED,
  stagedCoupon: null,
});

export const workflowReducer = (
  state: BillingWorkflowState,
  action: BillingWorkflowAction
): BillingWorkflowState => {
  switch (action.type) {
    case "open-plan-change":
    case "preselect-plan-change":
      return (
        blockIfActive(state) ?? {
          ...state,
          dialog: { type: "plan-change", selection: action.selection },
        }
      );
    case "preselect-smart-pricing-kickoff":
      return (
        blockIfActive(state) ?? {
          ...state,
          dialog: {
            type: "smart-pricing-kickoff",
            selection: action.selection,
            origin: "direct",
          },
        }
      );
    case "open-smart-pricing-kickoff":
      return state.dialog.type === "plan-change"
        ? {
            ...state,
            dialog: {
              type: "smart-pricing-kickoff",
              selection: state.dialog.selection,
              origin: "plan-change",
            },
          }
        : state;
    case "cancel-smart-pricing-kickoff":
      return state.dialog.type === "smart-pricing-kickoff"
        ? {
            ...state,
            dialog:
              state.dialog.origin === "direct"
                ? CLOSED
                : {
                    type: "plan-change",
                    selection: state.dialog.selection,
                  },
          }
        : state;
    case "show-smart-pricing-whats-next":
      return state.dialog.type === "command-blocked"
        ? state
        : {
            ...state,
            dialog: {
              type: "smart-pricing-whats-next",
              orderVolumeTier: action.orderVolumeTier,
              tierPurchased: action.tierPurchased,
            },
          };
    case "update-selection":
      if (isActive(state.command)) {
        return { ...state, dialog: blockedDialog() };
      }
      return state.dialog.type === "plan-change"
        ? {
            ...state,
            dialog: { ...state.dialog, selection: action.selection },
          }
        : state;
    case "stage-coupon":
      return { ...state, stagedCoupon: action.coupon };
    case "request-entity-action":
      return (
        blockIfActive(state) ?? {
          ...state,
          dialog: dialogForEntityAction(action.action),
        }
      );
    case "open-core-module-cancel":
      return (
        blockIfActive(state) ?? {
          ...state,
          dialog: {
            type: "cancel-core-module",
            entityId: action.entityId,
            expectedCancellationAt: action.expectedCancellationAt,
            planEntityId: action.planEntityId,
            step: "warning",
          },
        }
      );
    case "advance-core-module-cancel":
      return state.dialog.type === "cancel-core-module" &&
        state.dialog.step === "warning"
        ? { ...state, dialog: { ...state.dialog, step: "confirm" } }
        : state;
    case "dismiss-dialog":
      return { ...state, dialog: CLOSED, stagedCoupon: null };
    // Scoped to the dialog it acknowledges: a blocked-dialog CTA that opens
    // another dialog must survive the acknowledgement IgDialog fires after the
    // click, which would otherwise close the dialog the CTA just opened.
    case "acknowledge-blocked":
      return state.dialog.type === "command-blocked"
        ? { ...state, dialog: CLOSED }
        : state;
    case "organization-changed":
      return initialBillingWorkflowState;
    case "show-command-blocked":
      return {
        ...state,
        dialog: {
          type: "command-blocked",
          acknowledgeLabel: action.acknowledgeLabel,
          body: action.body,
          title: action.title,
          ...(action.recovery ? { recovery: action.recovery } : {}),
        },
      };
    case "command-started":
      return (
        blockIfActive(state) ?? {
          approvalReturn: state.approvalReturn,
          dialog: state.dialog.type === "plan-change" ? state.dialog : CLOSED,
          stagedCoupon:
            action.plan.operation === "apply-coupon"
              ? null
              : state.stagedCoupon,
          command: {
            status: "submitting",
            awaitsNamedShopifyApproval: false,
            commandId: null,
            orgId: action.orgId,
            overlay: action.plan.optimisticEvent,
            plan: action.plan,
          },
        }
      );
    // Arming is a one-shot: the marker is consumed on arrival, so a second arm
    // in the same session would be a replay of a return that already settled.
    case "approval-return-armed":
      return state.approvalReturn.status === "idle"
        ? {
            ...state,
            approvalReturn: {
              status: "waiting",
              approvalKind: action.approvalKind,
              armedAt: action.armedAt,
              pollsSnapshot: false,
            },
          }
        : state;
    case "approval-return-polling":
      return state.approvalReturn.status === "waiting"
        ? {
            ...state,
            approvalReturn: { ...state.approvalReturn, pollsSnapshot: true },
          }
        : state;
    case "approval-return-settled":
      return state.approvalReturn.status === "waiting"
        ? {
            ...state,
            approvalReturn: { status: "settled", outcome: action.outcome },
          }
        : state;
    case "command-accepted":
      return state.command.status === "submitting"
        ? {
            ...state,
            command: {
              ...state.command,
              commandId: action.commandId,
              status: state.command.plan.mayAwaitShopifyApproval
                ? "awaiting-approval"
                : "reconciling",
            },
          }
        : state;
    case "shopify-approval-named":
      return isActive(state.command)
        ? {
            ...state,
            command: { ...state.command, awaitsNamedShopifyApproval: true },
          }
        : state;
    case "shopify-approval-timed-out":
      return state.command.status === "awaiting-approval"
        ? finishCommand(state)
        : state;
    case "raw-snapshot-observed":
      return isActive(state.command) &&
        isOptimisticEventConfirmed(action.snapshot, state.command.overlay)
        ? finishCommand(state)
        : state;
    case "server-confirmed":
    case "mutation-failed":
      return isActive(state.command) ? finishCommand(state) : state;
    case "terminal-command-failure":
      return isActive(state.command) &&
        state.command.commandId === action.commandId &&
        state.command.orgId === action.orgId
        ? finishCommand(state)
        : state;
    case "websocket-observed":
      if (
        !isActive(state.command) ||
        state.command.commandId == null ||
        !isMatchingCommandMessage(
          {
            commandId: state.command.commandId,
            orgId: state.command.orgId,
          },
          action.message
        )
      ) {
        return state;
      }
      if (isTerminalCommandFailure(action.message.status)) {
        return finishCommand(state);
      }
      return action.message.status === "completed"
        ? {
            ...state,
            command: { ...state.command, status: "reconciling" },
          }
        : state;
  }
};

export const selectWorkflowSnapshot = (
  rawSnapshot: BillingSnapshot,
  state: BillingWorkflowState,
  orgId?: string
): BillingSnapshot =>
  isActive(state.command) &&
  (orgId === undefined || state.command.orgId === orgId)
    ? applyOptimisticEvent(rawSnapshot, state.command.overlay)
    : rawSnapshot;
